import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axiosInstance from "../../hooks/axios";
import InputWithLabel from "../../components/form/input/InputWithLabel";
import PageBreadcrumb from "../../components/common/PageBreadCrumb";
import { LocationChargeFormData } from "../../types/locationCharge";

export default function LocationChargeForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = Boolean(id);

  const [formData, setFormData] = useState<LocationChargeFormData>({
    postCode: "",
    price: null,
    status: true
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (isEditMode) {
      fetchLocationChargeData();
    }
  }, [id]);

  const fetchLocationChargeData = async () => {
    try {
      const response = await axiosInstance.get(`/locationcharges/${id}`);
      const data = response.data;
      setFormData({
        postCode: data.postCode || "",
        price: data.price,
        status: data.status ?? true
      });
    } catch (error) {
      setError("Failed to fetch location charge data");
      console.error("Error fetching location charge:", error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      if (isEditMode) {
        await axiosInstance.put(`/locationcharges/${id}`, formData);
      } else {
        await axiosInstance.post("/locationcharges", formData);
      }
      navigate("/location-charges");
    } catch (error: any) {
      setError(error.response?.data?.message || "Failed to save location charge");
      console.error("Error saving location charge:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' 
        ? (e.target as HTMLInputElement).checked 
        : name === 'price' 
          ? value === '' ? null : Number(value)
          : value
    }));
  };

  return (
    <div className="w-full space-y-6">
      <PageBreadcrumb pageTitle={isEditMode ? "Edit Location Charge" : "Add Location Charge"} />

      <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-theme-sm dark:border-gray-800 dark:bg-white/[0.03] lg:p-6">
        {error && (
          <div className="mb-4 rounded-xl border border-error-200 bg-error-50 px-4 py-3 text-sm text-error-700 dark:border-error-500/30 dark:bg-error-500/10 dark:text-error-400">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="w-full space-y-6">
          <div className="grid grid-cols-1 gap-6">
            <InputWithLabel
              label="Post Code"
              name="postCode"
              value={formData.postCode}
              onChange={handleInputChange}
              required
            />

            <InputWithLabel
              label="Price"
              name="price"
              type="number"
              step="0.01"
              value={formData.price === null ? '' : formData.price}
              onChange={handleInputChange}
            />

            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="status"
                name="status"
                checked={formData.status}
                onChange={handleInputChange}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="status" className="text-sm font-medium text-gray-700">
                Active
              </label>
            </div>
          </div>

          <div className="flex justify-end space-x-4">
            <button
              type="button"
              onClick={() => navigate("/location-charges")}
              className="px-4 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="inline-flex items-center justify-center rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-brand-600 disabled:opacity-50"
            >
              {loading ? "Saving..." : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
} 